import { Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { produce } from "immer";
import React, { useCallback, useContext, useState } from "react";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from "../../../lib/helper";
import ObjectUtils from "../../../utils/ObjectUtils";
import MultiSelector from "../../atoms/MultiSelector";
import { Dashboard } from "../../../types/files";
import { scrollbarStyle } from "../../../shared/dashboard";

const useStyles = makeStyles((theme) => ({
    title: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    subtitle: {
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: '600',
        color: theme.colors.neutral['500'],
    },
}));

const TagsQuery: React.FC<{}> = () => {
    const classes = useStyles();
    const { tagGroups } = useContext(GroupSettingsContext);
    const { isPublicView, dashboard } = useContext(DashboardContext);
    const [updating, setUpdating] = useState<string>('');

    const lookUpTagsSelection = useCallback((dashboard: Dashboard|null, title: string) =>
        dashboard?.tagGroups?.find(dashboard => dashboard.title.toLowerCase() === title.toLowerCase())?.tags || []
    , []);

    const handleSelectTags = useCallback(async (selections: string[], title: string) => {
        const lookUpTagGroup = dashboard!.tagGroups?.find(dashboard => dashboard.title.toLowerCase() === title.toLowerCase());

        if (!ObjectUtils.equalObjects(lookUpTagGroup?.tags, selections)) {
            setUpdating(title);
            await updateDashboardFunc(produce(dashboard!, (draft) => {
                if (!draft.tagGroups?.find(group => group.title.toLowerCase() === title.toLowerCase())) {
                    draft.tagGroups = [...(draft.tagGroups || []), { title, tags: selections, manualOverride: true }];
                } else {
                    const groupIndex = draft.tagGroups.findIndex(group => group.title.toLowerCase() === title.toLowerCase());
                    draft.tagGroups[groupIndex].tags = selections;
                    draft.tagGroups[groupIndex].manualOverride = true;
                }
            })).then(() => setUpdating(''));
        }
    }, [dashboard]);

    return (<>
        <Stack spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%" maxHeight="416px"
            sx={{ overflow: 'hidden' }}>
            <Typography className={classes.title}>
                {'Tags'}
            </Typography>
            <Stack spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%" height="100%"
                sx={{ overflowY: 'auto', ...scrollbarStyle }}>
                {tagGroups.map(({title, tags, tagsPerDashboard}, i) => (
                    <Stack spacing={2} alignItems="flex-start" justifyContent="flex-start" pl={2}
                        key={'tags-query-58-' + i}>
                        <Typography className={classes.subtitle}>
                            {title}
                        </Typography>
                        <MultiSelector
                            mainOptions={tags}
                            selections={lookUpTagsSelection(dashboard, title)}
                            onSelection={(selections) => handleSelectTags(selections, title)}
                            readOnly={isPublicView}
                            loading={updating === title}
                            limit={tagsPerDashboard}
                            fullSpan />
                    </Stack>
                ))}
            </Stack>
        </Stack>
    </>);
}

export default TagsQuery;
